// Rich exam-result visualization built from the stored ThinkExam `result_data`
// (subject breakdown, correct/wrong/unattempted, accuracy, time, rank, percentile).
// Renders null when the result has no detailed data, so callers can keep a simple score line.

import { Target, Clock, Trophy, TrendingUp } from "lucide-react";

interface Subject {
  name: string;
  scored: number;
  total?: number;
  percentage?: number;
}

interface Parsed {
  correct?: number;
  wrong?: number;
  unattempted?: number;
  totalQ?: number;
  accuracy?: number;
  timeSeconds?: number;
  rank?: number;
  percentile?: number;
  subjects: Subject[];
}

const num = (v: any): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const parseExamResult = (exam: any): Parsed => {
  const rd = exam?.result_data || {};
  const student = rd.student || {};

  const correct = num(student.rightQue);
  const wrong = num(student.wrongQue);
  const unattempted = num(student.leftQue);
  const summed = (correct ?? 0) + (wrong ?? 0) + (unattempted ?? 0);
  const totalQ = num(student.totalQue) ?? (summed > 0 ? summed : undefined);

  let subjects: Subject[] = [];
  if (Array.isArray(student.subjectData) && student.subjectData.length) {
    subjects = student.subjectData.map((s: any) => ({
      name: s.subjectName || "Subject",
      scored: num(s.studentScoredMarks) ?? 0,
      total: num(s.subjectTotalMarks),
      percentage: num(s.studentPercentage),
    }));
  } else if (Array.isArray(rd.subject_name) && rd.subject_name.length) {
    subjects = rd.subject_name.map((nm: string, i: number) => ({
      name: nm || "Subject",
      scored: num(rd.subject_marks?.[i]) ?? 0,
    }));
  }

  return {
    correct,
    wrong,
    unattempted,
    totalQ,
    accuracy: num(student.accuracy),
    timeSeconds: num(student.totalTime) ?? num(student.proTime),
    rank: num(student.rank),
    percentile: num(student.percentile),
    subjects,
  };
};

const fmtTime = (s?: number) => {
  if (s == null) return undefined;
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return sec ? `${m}m ${sec}s` : `${m}m`;
};

const Ring = ({ percent, pass }: { percent: number; pass: boolean }) => {
  const r = 44;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const off = c - (clamped / 100) * c;
  const full = clamped >= 100;
  const gid = pass ? "ring-pass" : "ring-fail";
  return (
    <div className="relative h-[116px] w-[116px] shrink-0">
      <svg width="116" height="116" viewBox="0 0 116 116">
        <defs>
          <linearGradient id="ring-pass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
          <linearGradient id="ring-fail" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#dc2626" />
          </linearGradient>
        </defs>
        <circle cx="58" cy="58" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="9" />
        {/* Full circle at 100% avoids the round-cap overlap seam; partial arc keeps rounded ends. */}
        {full ? (
          <circle cx="58" cy="58" r={r} fill="none" stroke={`url(#${gid})`} strokeWidth="9" />
        ) : (
          <circle
            cx="58" cy="58" r={r} fill="none"
            stroke={`url(#${gid})`} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 58 58)"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold leading-none text-foreground">{Math.round(percent)}<span className="text-sm">%</span></span>
        <span className={`mt-0.5 text-[9px] font-bold uppercase tracking-wider ${pass ? "text-green-600" : "text-red-600"}`}>Score</span>
      </div>
    </div>
  );
};

const STAT_STYLES: Record<string, { icon: JSX.Element; iconCls: string }> = {
  Accuracy:   { icon: <Target className="h-4 w-4" />,      iconCls: "bg-violet-100 text-violet-600" },
  Time:       { icon: <Clock className="h-4 w-4" />,       iconCls: "bg-blue-100 text-blue-600" },
  Rank:       { icon: <Trophy className="h-4 w-4" />,      iconCls: "bg-amber-100 text-amber-600" },
  Percentile: { icon: <TrendingUp className="h-4 w-4" />,  iconCls: "bg-green-100 text-green-600" },
};

const StatCard = ({ label, value }: { label: string; value: string }) => {
  const s = STAT_STYLES[label];
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2">
      <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${s?.iconCls ?? "bg-muted text-muted-foreground"}`}>
        {s?.icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm font-bold text-foreground tabular-nums leading-tight">{value}</p>
      </div>
    </div>
  );
};

const LegendPill = ({ dot, label, count }: { dot: string; label: string; count: number }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs">
    <span className={`h-2 w-2 rounded-full ${dot}`} />
    <span className="text-muted-foreground">{label}</span>
    <span className="font-bold text-foreground tabular-nums">{count}</span>
  </span>
);

const ExamResultDetail = ({ exam }: { exam: any }) => {
  const p = parseExamResult(exam);
  const pass = exam?.status === "passed";
  const percent = num(exam?.percentage) ?? (p.totalQ && p.correct != null ? Math.round((p.correct / p.totalQ) * 100) : 0);

  const hasQuestions = p.totalQ != null && (p.correct != null || p.wrong != null || p.unattempted != null);
  const stats = [
    p.accuracy != null ? { label: "Accuracy", value: `${Math.round(p.accuracy)}%` } : null,
    p.timeSeconds != null ? { label: "Time", value: fmtTime(p.timeSeconds)! } : null,
    p.rank != null ? { label: "Rank", value: `#${p.rank}` } : null,
    p.percentile != null ? { label: "Percentile", value: `${Math.round(p.percentile)}` } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  // Nothing detailed to show — let the caller render its plain score line.
  if (!hasQuestions && p.subjects.length === 0 && stats.length === 0) return null;

  const totalQ = p.totalQ || 1;
  const seg = (n?: number) => `${((n ?? 0) / totalQ) * 100}%`;

  return (
    <div className="mt-3 rounded-2xl border border-border bg-card p-5 space-y-5">
      {/* Score + performance bar */}
      <div className="flex items-center gap-5">
        <Ring percent={percent} pass={pass} />
        <div className="min-w-0 flex-1 space-y-3">
          {hasQuestions ? (
            <>
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                {(p.correct ?? 0) > 0 && <div className="bg-green-500" style={{ width: seg(p.correct) }} />}
                {(p.wrong ?? 0) > 0 && <div className="bg-red-500" style={{ width: seg(p.wrong) }} />}
                {(p.unattempted ?? 0) > 0 && <div className="bg-muted-foreground/40" style={{ width: seg(p.unattempted) }} />}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <LegendPill dot="bg-green-500" label="Correct" count={p.correct ?? 0} />
                <LegendPill dot="bg-red-500" label="Wrong" count={p.wrong ?? 0} />
                <LegendPill dot="bg-muted-foreground/40" label="Unattempted" count={p.unattempted ?? 0} />
                <span className="ml-auto text-xs font-medium text-muted-foreground">Total {p.totalQ} questions</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Detailed question breakdown not available for this attempt.</p>
          )}
        </div>
      </div>

      {p.subjects.length > 0 && (
        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Subject breakdown</p>
          {p.subjects.map((s, i) => {
            const pct = s.percentage ?? (s.total ? Math.round((s.scored / s.total) * 100) : undefined);
            return (
              <div key={`${s.name}-${i}`} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-foreground truncate">{s.name}</span>
                  <span className="text-muted-foreground shrink-0 ml-2 tabular-nums">
                    {s.scored}{s.total != null ? `/${s.total}` : ""}{pct != null ? ` · ${pct}%` : ""}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct ?? 0}%`, background: "linear-gradient(90deg, #677be6, #7450a9)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 border-t border-border pt-4">
          {stats.map((s) => <StatCard key={s.label} label={s.label} value={s.value} />)}
        </div>
      )}
    </div>
  );
};

export default ExamResultDetail;
